import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { Lot } from '../../shared/inventory/model/lot.model';
import { Transaction } from '../../shared/inventory/model/transaction.model';
import { InventoryUnit } from '../../shared/inventory/model/inventory-unit.model';
import { TransactionService } from '../../shared/inventory/service/transaction.service';
import { LotService } from '../../shared/inventory/service/lot.service';
import { InventoryService } from '../../shared/inventory/service/inventory.service';
import { ParamContext } from '../../shared/service/param.context';
import { SearchComposite } from '../../shared/model/search-composite';
import { AlertService } from '../../shared/alert/alert.service';
import { PopupService } from '../../shared/modal/popup.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GermplasmManagerContext } from '../germplasm-manager.context';
import { SearchOrigin, SearchOriginComposite } from '../../shared/model/Search-origin-composite';
import { LocationService } from '../../shared/location/service/location.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-lot-creation-dialog',
    templateUrl: './lot-creation-dialog.component.html'
})
export class LotCreationDialogComponent implements OnInit, OnDestroy {

    /** Indicates that the creation process has finished */
    isSuccess = false;

    lot: Lot;
    model = { stockIdPrefix: '' };
    deposit: Transaction;
    searchRequestId;
    searchOrigin: SearchOrigin;

    units: Promise<InventoryUnit[]>;
    unitsVariables: InventoryUnit[];

    initialDepositRequired = false;
    initialDepositCheckbox = false;

    storageLocIdSelected;

    isConfirmDeposit = false;
    isReuseSrcObservationUnitId = false;
    showReuseSrcObsUnitToggle = false;
    isLoading = false;

    openedFromWorkbench: boolean;
    isSeedAmountUndefined = false;

    subscriptionManager = new SubscriptionsManager();

    constructor(private activatedRoute: ActivatedRoute,
                private jhiLanguageService: JhiLanguageService,
                private transactionService: TransactionService,
                private inventoryService: InventoryService,
                private lotService: LotService,
                private eventManager: JhiEventManager,
                private paramContext: ParamContext,
                private germplasmManagerContext: GermplasmManagerContext,
                private activeModal: NgbActiveModal,
                private alertService: AlertService,
                private locationService: LocationService
    ) {
        this.paramContext.readParams();
        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (queryParams.searchRequestId) {
            this.searchRequestId = queryParams.searchRequestId;
        }
        if ( queryParams.searchOrigin) {
            this.searchOrigin = queryParams.searchOrigin;
            this.openedFromWorkbench = (this.searchOrigin === SearchOrigin.GERMPLASM_SEARCH) ? true : false;
        }

        if (this.searchOrigin === SearchOrigin.MANAGE_STUDY_SOURCE || this.searchOrigin === SearchOrigin.MANAGE_STUDY_PLOT) {
            // searchOrigin is MANAGE_STUDY_SOURCE or MANAGE_STUDY_PLOT indicate that Lot Creation page is called
            // from Study Manager and the deposit is required.
            this.initialDepositRequired = true;
        }

        this.showReuseSrcObsUnitToggle = this.searchOrigin === SearchOrigin.MANAGE_STUDY_SOURCE;

        this.lot = new Lot();
        this.deposit = new Transaction();

        this.units = this.inventoryService.queryUnits().toPromise();
        this.units.then((units) => this.unitsVariables = units);
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit() {
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    save() {
        this.isLoading = true;
        this.lot.locationId = this.storageLocIdSelected;

        const lotGeneratorBatchRequest = {
            searchComposite: this.getSearchComposite(),
            lotGeneratorInput: Object.assign({
                generateStock: true,
                stockPrefix: this.model.stockIdPrefix,
                isReuseSrcObservationUnitId: this.isReuseSrcObservationUnitId,
                isUseSrcObsUnitId: this.isReuseSrcObservationUnitId ? 1 : 0
            }, this.lot)
        };
        this.subscriptionManager.add = this.lotService.createLots(lotGeneratorBatchRequest)
            .subscribe(
                (res) => this.createDeposit(res),
                () => this.isLoading = false);
    }

    private getSearchComposite(): SearchComposite<any, number> {
        if (this.searchOrigin === SearchOrigin.GERMPLASM_SEARCH) {
            return this.germplasmManagerContext.searchComposite;
        } else if (this.searchOrigin === SearchOrigin.MANAGE_STUDY_SOURCE || this.searchOrigin === SearchOrigin.MANAGE_STUDY_PLOT) {
            const searchTypeComposite = new SearchOriginComposite(this.searchRequestId, this.searchOrigin);
            return {
                searchRequest: searchTypeComposite
            };
        }
    }

    private createDeposit(lotUUIDs: string[]) {
        if (this.initialDepositRequired || this.initialDepositCheckbox) {
            const lotDepositRequest = {
                selectedLots: <SearchComposite<any, string>>({ searchRequest: null, itemIds: lotUUIDs }),
                notes: this.deposit.notes,
                depositsPerUnit: {},
                searchComposite: this.getSearchComposite()
            };
            this.units.then((units) => {
                const lotUnit = units.filter((unit) => unit.id === this.lot.unitId.toString());
                lotDepositRequest.depositsPerUnit[lotUnit[0].name] = this.deposit.amount;
                if (this.isConfirmDeposit) {
                    this.subscriptionManager.add = this.transactionService.createConfirmedDeposits(lotDepositRequest).subscribe(
                        (res) => this.onSaveSuccess(lotUUIDs),
                        () => this.isLoading = false);
                } else {
                    this.subscriptionManager.add = this.transactionService.createPendingDeposits(lotDepositRequest).subscribe(
                        (res) => this.onSaveSuccess(lotUUIDs),
                        () => this.isLoading = false);
                }
            });
        } else {
            this.onSaveSuccess(lotUUIDs);
        }
    }

    isDepostiRequired() {
        return this.initialDepositRequired || this.initialDepositCheckbox;
    }

    private onSaveSuccess(lotUUIDs: string[]) {
        this.isSuccess = true;
        this.isLoading = false;
        this.alertService.success('lot-creation.success', { param: lotUUIDs.length }, null);
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: '' });
        this.activeModal.close();
    }

    close() {
        this.activeModal.dismiss();
    }

    unitChanged() {
        const seedAmountUndefinedUnit = this.unitsVariables.find((unit) => unit.name === 'SEED_AMOUNT_UNDEFINED');

        if (seedAmountUndefinedUnit.id === String(this.lot.unitId)) {
            this.isSeedAmountUndefined = true;
            this.isConfirmDeposit = false;
            this.deposit.amount = 0;
        } else {
            this.isSeedAmountUndefined = false;
            this.deposit.amount = null;
        }
    }

}

@Component({
    selector: 'jhi-lot-creation-popup',
    template: ''
})

export class LotCreationPopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(private alertService: AlertService,
                private route: ActivatedRoute,
                private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe((params) => {
            this.popupService
                .open(LotCreationDialogComponent as Component);
        });
    }

    ngOnDestroy() {
        if (this.routeSub) {
            this.routeSub.unsubscribe();
        }
    }

}
